import { styled } from "styled-components";

import { topicsApiSlice, useLazyDeleteTopicAccessQuery } from "api/topicsApi.slice";
import { ActionButtonItem, DeleteIcon, Spinner } from "common/components";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import { useTranslate } from "common/utilities/useTranslate";
import { getUserId } from "domains/authentication/selectors";
import { getMapIsPending } from "domains/createMapDetail/selectors";
import { activateModal } from "domains/modal/actions";
import { topicSlice } from "domains/topic/topic.slice";
import { iconStyle } from "domains/topicDetail/components/actionButtons/iconStyle";
import type { Topic } from "entities/topics/types";

const StyledDeleteIcon = styled(DeleteIcon)`
  ${iconStyle}
`;

const StyledSpinner = styled(Spinner)`
  ${iconStyle}
`;

type RemoveMapActionButtonProps = { topic: Topic };

export const RemoveMapActionButton = ({ topic }: RemoveMapActionButtonProps) => {
  const dispatch = useDispatch();
  const go = useGo();
  const [deleteTopicAccess] = useLazyDeleteTopicAccessQuery();
  const isPending = useSelector(getMapIsPending);
  const userId = useSelector(getUserId);
  const translate = useTranslate();

  const deleteMap = () =>
    dispatch(activateModal({ id: "ConfirmDeleteMap", params: { name: topic.name, topicId: topic.id } }));

  const removeCustomMap = () =>
    void deleteTopicAccess({ topic_id: topic.id }, false).then((result) => {
      if (!result.isSuccess) return;
      dispatch(topicsApiSlice.util.invalidateTags(["CustomMaps"]));
      dispatch(topicSlice.actions.decrementCustomTopicsCount());
      go("/create");
    });

  return topic.provider !== undefined && topic.provider.id === userId ? (
    <ActionButtonItem
      dataTestId="delete-custom-map-button"
      icon={topic.isRemoved ? <StyledSpinner /> : <StyledDeleteIcon />}
      isPending={isPending}
      label={translate("app.topic.actions.delete")}
      onClick={deleteMap}
    />
  ) : topic.permission !== undefined && topic.permission !== "public" ? (
    <ActionButtonItem
      icon={topic.isRemoved ? <StyledSpinner /> : <StyledDeleteIcon />}
      isPending={isPending}
      label={translate("app.topic.actions.remove")}
      onClick={removeCustomMap}
    />
  ) : null;
};
